import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

export function useScrollNav(targetElementId = 'scroll-container') {
  const [isAtTop, setIsAtTop] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const lastYRef = useRef(0);
  const scrollElementRef = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    // Try multiple ways to find the scroll container (React Native Web uses nativeID -> id)
    const findScrollElement = () => {
      if (targetElementId) {
        // Try by id first
        let el = document.getElementById(targetElementId);
        if (el) return el;
        
        // Try by data-testid (another RN Web approach)
        el = document.querySelector(`[data-testid="${targetElementId}"]`);
        if (el) return el;
      }
      return null;
    };

    // Delay finding element slightly to ensure DOM is ready
    const initTimeout = setTimeout(() => {
      scrollElementRef.current = findScrollElement();
    }, 100);
    
    const getScrollY = () => {
      const scrollElement = scrollElementRef.current;
      
      // Check the scroll element first
      if (scrollElement && scrollElement.scrollTop > 0) {
        return scrollElement.scrollTop;
      }
      
      // Also check document/window scroll
      return Math.max(
        window.scrollY || 0,
        window.pageYOffset || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0,
        scrollElement?.scrollTop || 0
      );
    };

    const initialY = getScrollY();
    setIsAtTop(initialY < 50);
    lastYRef.current = initialY;

    let ticking = false;

    const onScroll = (e) => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const y = getScrollY();

        setIsAtTop(y < 50);

        if (y < 50) {
          setIsVisible(true);
        } else {
          // Scroll up = show navbar, scroll down = hide navbar
          const delta = y - lastYRef.current;
          if (delta < -5) {
            // Scrolling UP
            setIsVisible(true);
          } else if (delta > 5) {
            // Scrolling DOWN
            setIsVisible(false);
          }
        }

        lastYRef.current = y;
        ticking = false;
      });
    };

    // Listen on window for scroll events (most reliable)
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    
    // Also try to listen on the specific element after it's found
    const elementListenTimeout = setTimeout(() => {
      const el = scrollElementRef.current || findScrollElement();
      if (el) {
        el.addEventListener('scroll', onScroll, { passive: true });
      }
    }, 150);

    return () => {
      clearTimeout(initTimeout);
      clearTimeout(elementListenTimeout);
      window.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('scroll', onScroll, { capture: true });
      
      const el = scrollElementRef.current;
      if (el) {
        el.removeEventListener('scroll', onScroll);
      }
    };
  }, [targetElementId]);

  return { isAtTop, isVisible };
}

